/**
 * Mode Service
 * Handles operations related to mode switching and mode-specific functionality
 */
import { useState, useCallback, useEffect, useRef } from 'react';

// Mode constants
export const MODES = [
  'Setup Points',    // Setup mode - Add and manage location points
  'Custom Path',     // Custom mode - Manually plan path
  'Auto Path',       // Auto mode - Algorithm solves path
  'Compare Paths'    // Compare mode - Compare different paths
];

/**
 * Hook for managing mode switching state and functions
 * @returns {Object} Mode state and control functions
 * @property {string} currentMode - Current active mode
 * @property {Function} setCurrentMode - Mode setter function
 * @property {import('react').MutableRefObject<string>} currentModeRef - Ref to current mode
 */
export const useModeSwitching = () => {
  const [currentMode, setCurrentMode] = useState(MODES[0]);
  const currentModeRef = useRef(currentMode);
  
  // Update mode reference when mode changes
  useEffect(() => {
    currentModeRef.current = currentMode;
  }, [currentMode]);
  
  return {
    currentMode, 
    setCurrentMode,
    currentModeRef
  };
};

/**
 * Get the name of the current active mode
 * @param {string} currentMode - Current mode value
 * @returns {string} Active mode name or default mode
 */
export const getActiveModeName = (currentMode) => currentMode || MODES[0];

/**
 * Mode check functions
 * These functions determine if a given mode matches a specific mode type
 */
export const isAutoMode = (mode) => mode === MODES[2];
export const isCustomMode = (mode) => mode === MODES[1];
export const isSetupMode = (mode) => mode === MODES[0];
export const isCompareMode = (mode) => mode === MODES[3]; 